import React, { useState, useEffect } from 'react';
import { tippspielAdminAPI } from '../../services/api';

const SCORING_FIELDS = [
  { key: 'exactScorePoints', label: 'Exaktes Ergebnis' },
  { key: 'correctGoalDifferencePoints', label: 'Richtige Tordifferenz' },
  { key: 'correctTendencyPoints', label: 'Richtige Tendenz' },
  { key: 'correctDrawPoints', label: 'Richtiges Unentschieden' },
  { key: 'wrongTipPoints', label: 'Falscher Tipp' },
  { key: 'missedTipPoints', label: 'Verpasster Tipp' },
];

const RESULT_MODES = [
  { value: 'regular', label: 'Regular (90 Min.)' },
  { value: 'afterExtraTime', label: 'Nach Verlängerung' },
  { value: 'officialFinal', label: 'Offizielles Endergebnis' },
  { value: 'mfleagueDefault', label: 'MFLeague Standard' },
];

const TippspielScoringSettings = () => {
  const [scoring, setScoring] = useState({ scoringRules: {}, scoringVersion: 1 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalcScope, setRecalcScope] = useState('future');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    tippspielAdminAPI.getScoring()
      .then((res) => setScoring(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Laden fehlgeschlagen'))
      .finally(() => setLoading(false));
  }, []);

  const rules = scoring.scoringRules || {};

  const updateRule = (key, value) => {
    setScoring((s) => ({
      ...s,
      scoringRules: { ...s.scoringRules, [key]: value },
    }));
  };

  const handleSave = async (recalculate) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await tippspielAdminAPI.updateScoring({
        scoringRules: scoring.scoringRules,
        bumpVersion: true,
      });
      if (recalculate) {
        const r = await tippspielAdminAPI.recalculate({ scope: recalcScope });
        setMessage(`Gespeichert. ${r.data.scored} Tipps in ${r.data.matchCount} Spielen neu gewertet.`);
      } else {
        setMessage('Punkteregeln gespeichert (neue Version für künftige Tipps).');
      }
      const fresh = await tippspielAdminAPI.getScoring();
      setScoring(fresh.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><span className="loading loading-spinner loading-lg text-wacker" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="section-title"><span>Punkte & Auswertung</span></h2>
        <p className="text-sm text-white/45 mt-1">Version: {scoring.scoringVersion}</p>
      </div>

      {error && <div className="alert-error-dark">{error}</div>}
      {message && <div className="alert-success-dark">{message}</div>}

      <div className="glass-card p-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {SCORING_FIELDS.map(({ key, label }) => (
            <div key={key}>
              <label className="label-dark text-xs">{label}</label>
              <input
                type="number"
                className="admin-input py-2"
                value={rules[key] ?? 0}
                onChange={(e) => updateRule(key, parseInt(e.target.value, 10) || 0)}
              />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div>
            <label className="label-dark">Ergebnis-Modus</label>
            <select
              className="admin-select"
              value={rules.resultMode || 'regular'}
              onChange={(e) => updateRule('resultMode', e.target.value)}
            >
              {RESULT_MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mt-6">
          {[
            ['evaluateCancelledMatches', 'Abgesagte Spiele werten'],
            ['reEvaluateOnResultChange', 'Bei Ergebnisänderung neu werten'],
            ['missedCountsAsZero', 'Verpasster Tipp = 0 Punkte'],
            ['countPenaltyShootout', 'Elfmeterschießen zählen'],
            ['bonusPointsEnabled', 'Bonuspunkte aktiv'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer text-white/80 text-sm">
              <input
                type="checkbox"
                className="admin-checkbox"
                checked={Boolean(rules[key])}
                onChange={(e) => updateRule(key, e.target.checked)}
              />
              {label}
            </label>
          ))}
        </div>

        <div className="border-t border-white/10 mt-6 pt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="label-dark text-xs">Beim Speichern neu werten</label>
            <select className="admin-select py-2 text-sm" value={recalcScope} onChange={(e) => setRecalcScope(e.target.value)}>
              <option value="future">Nur künftige Spiele</option>
              <option value="all">Alle Spiele</option>
            </select>
          </div>
          <button type="button" className="btn-wacker text-sm py-2 px-4" disabled={saving} onClick={() => handleSave(false)}>
            Nur für Zukunft speichern
          </button>
          <button type="button" className="btn-wacker-outline text-sm py-2 px-4" disabled={saving} onClick={() => handleSave(true)}>
            Speichern & neu werten
          </button>
        </div>
      </div>
    </div>
  );
};

export default TippspielScoringSettings;
